import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import "../styles/UserList.css";
import DefaultEmployeeProfile from "../images/DefaultEmployeeProfile.svg";
import deleteIcon from "../images/deleteIcon.svg";
import editIcon from "../images/editIcon.svg";
import generateInvoice from "../images/generateInvoice.svg";
import cancelInvoice from "../images/cancel.svg";
import archiveIcon from "../images/archive.svg";
import unarchiveIcon from "../images/unarchive.svg";
import dustbin from "../images/dustbin.svg";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import {
  deniedActionClass,
  usePermissionGuard,
} from "../hooks/usePermissionGuard";
import { PERMISSIONS } from "../utils/rbac";

const formatYtd = (value) => {
  const numericValue = Number(value || 0);
  if (isNaN(numericValue)) return "0.00";
  return numericValue.toFixed(2);
};

const ConfirmDeleteModal = ({ id, onDeleteUser, onClose }) => {
  return (
    <div className="custom-modal">
      <div className="modal-content">
        <div className="left-container">
          <img src={dustbin} alt="Dustbin" className="dustbin-image" />
        </div>
        <div className="right-container">
          <p className="delete-warning-p">{`Are you sure you want to delete this profile?`}</p>
          <div className="button-container">
            <button
              className="delete-button"
              onClick={() => {
                onDeleteUser(id);
                onClose();
              }}
            >
              Delete
            </button>
            <button className="cancel-button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function UserList({
  users,
  onBlockUser,
  onDeleteUser,
  currentPage,
  pageSize,
  showActionLinks = true,
  startDate = "",
  endDate = "",
  rangeId = "",
  onZeroSubmission = () => {},
  showZeroSubmisson = false,
  showYtdColumn = false,
  isArchivedList = false,
  onArchiveUser = () => {},
  onUnarchiveUser = () => {},
}) {
  const startIndex = (currentPage - 1) * pageSize + 1;
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [cancelZeroUserId, setCancelZeroUserId] = useState(null);

  const navigate = useNavigate();
  const { guardAction, PermissionDialog, isAllowed } = usePermissionGuard();

  const canCreatePayslip = isAllowed(PERMISSIONS.CREATE_PAYSLIP);
  const canEditEmployee = isAllowed(PERMISSIONS.EDIT);
  const canDeleteEmployee = isAllowed(PERMISSIONS.DELETE);
  const canArchiveEmployee = isAllowed(PERMISSIONS.ARCHIVE);

  const handleDeleteUser = (id) => {
    guardAction(PERMISSIONS.DELETE, "delete employee", () => {
      setUserToDelete(id);
      setShowModal(true);
    });
  };

  const handlePopZeroSubmission = (id) => {
    guardAction(PERMISSIONS.CREATE_PAYSLIP, "generate zero invoice", () => {
      onZeroSubmission(id);
    });
  };

  const confirmDelete = () => {
    onDeleteUser(userToDelete);
    setShowModal(false);
  };

  const cancelDelete = () => {
    setUserToDelete(null);
    setShowModal(false);
  };

  const handleEditClick = (userId) => {
    setSelectedUserId(userId);
  };

  const handleOnClick = (id) => {
    navigate("/userProfile", { state: id });
  };

  const handleGenerateInvoice = (e, userId) => {
    e.preventDefault();
    guardAction(PERMISSIONS.CREATE_PAYSLIP, "generate invoice", () => {
      navigate(
        `/generateInvoice${rangeId ? `?rangeId=${rangeId}` : ""}`,
        { state: userId },
      );
    });
  };

  const handleEditEmployee = (e, userId) => {
    e.preventDefault();
    guardAction(PERMISSIONS.EDIT, "edit employee", () => {
      handleEditClick(userId);
      navigate("/editEmployee", { state: userId });
    });
  };

  const handleArchive = (userId) => {
    guardAction(PERMISSIONS.ARCHIVE, "archive employee", () => {
      onArchiveUser(userId);
    });
  };

  const handleUnarchive = (userId) => {
    guardAction(PERMISSIONS.ARCHIVE, "unarchive employee", () => {
      onUnarchiveUser(userId);
    });
  };

  return (
    <div className="user-list user-list-margin">
      <PermissionDialog />
      {userToDelete && (
        <ConfirmDeleteModal
          id={userToDelete}
          onDeleteUser={confirmDelete}
          onClose={cancelDelete}
        />
      )}
      <table className="user-table">
        <thead>
          <tr>
            <th>Employee Id</th>
            <th>Image</th>
            <th>Name</th>
            <th>Email</th>
            {showYtdColumn && <th>YTD Amount($)</th>}
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, index) => (
            <tr key={user.id}>
              <td className="" onClick={() => handleOnClick(user.id)}>
                <Link to={"/userProfile"} state={user.id} className="user-link">
                  #{user.id}
                </Link>
              </td>
              <td className="user-info" onClick={() => handleOnClick(user.id)}>
                <div className="user-container">
                  {user.image ? (
                    <div className="user-list-image">
                      <img src={user.image} alt="Employee Image" />
                    </div>
                  ) : (
                    <div className="user-list-image">
                      <img
                        src={DefaultEmployeeProfile}
                        alt="Default Employee Image"
                      />
                    </div>
                  )}
                </div>
              </td>
              <td onClick={() => handleOnClick(user.id)}>
                <Link to={"/userProfile"} state={user.id} className="user-link">
                  {user.name.charAt(0).toUpperCase() + user.name.slice(1)}
                </Link>
              </td>
              <td onClick={() => handleOnClick(user.id)}>
                <Link to={"/userProfile"} state={user.id} className="user-link">
                  {user.email}
                </Link>
              </td>
              {showYtdColumn && (
                <td onClick={() => handleOnClick(user.id)}>
                  {formatYtd(user.finalYTD)}
                </td>
              )}
              <td>
                {isArchivedList ? (
                  <>
                    {showActionLinks && (
                      <>
                        <Tippy content="Unarchive">
                          <img
                            src={unarchiveIcon}
                            alt="Unarchive Icon"
                            className={`edit-icon admin-panel-icon icon-margin ${deniedActionClass(canArchiveEmployee)}`}
                            onClick={() => handleUnarchive(user.id)}
                          />
                        </Tippy>
                        <Tippy content="Delete">
                          <img
                            src={deleteIcon}
                            alt="Delete Icon"
                            className={`delete-icon admin-panel-icon ${deniedActionClass(canDeleteEmployee)}`}
                            onClick={() => handleDeleteUser(user.id)}
                          />
                        </Tippy>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Link
                      to={`/generateInvoice${
                        rangeId ? `?rangeId=${rangeId}` : ""
                      }`}
                      state={user.id}
                      className={`user-link icon-margin ${deniedActionClass(canCreatePayslip)}`}
                      onClick={(e) => handleGenerateInvoice(e, user.id)}
                    >
                      <Tippy content="Generate Invoice">
                        <img
                          src={generateInvoice}
                          alt="Generate Invoice"
                          className="edit-icon admin-panel-icon"
                        />
                      </Tippy>
                    </Link>
                    {showZeroSubmisson && (
                      <>
                        <Link
                          style={{
                            marginLeft: "15px",
                          }}
                          className={deniedActionClass(canCreatePayslip)}
                          onClick={(e) => {
                            e.preventDefault();
                            handlePopZeroSubmission(user.id);
                          }}
                        >
                          <Tippy content="Generate Zero Invoice">
                            <img
                              src={cancelInvoice}
                              alt="Cancel Invoice"
                              className="cancelzero-icon"
                            />
                          </Tippy>
                        </Link>
                      </>
                    )}

                    {showActionLinks && (
                      <>
                        <Link
                          to={"/editEmployee"}
                          state={user.id}
                          className={`user-link icon-margin ${deniedActionClass(canEditEmployee)}`}
                          onClick={(e) => handleEditEmployee(e, user.id)}
                        >
                          <Tippy content="Edit">
                            <img
                              src={editIcon}
                              alt="Edit Icon"
                              className="edit-icon admin-panel-icon"
                              style={{width: "7%"}}
                            />
                          </Tippy>
                        </Link>

                        <Tippy content="Archive">
                          <img
                            src={archiveIcon}
                            alt="Archive Icon"
                            className={`edit-icon admin-panel-icon icon-margin ${deniedActionClass(canArchiveEmployee)}`}
                            onClick={() => handleArchive(user.id)}
                            style={{width: "7.5%"}}
                            />
                        </Tippy>

                        <Tippy content="Delete">
                          <img
                            src={deleteIcon}
                            alt="Delete Icon"
                            className={`delete-icon admin-panel-icon ${deniedActionClass(canDeleteEmployee)}`}
                            onClick={() => handleDeleteUser(user.id)}
                          />
                        </Tippy>
                      </>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default UserList;
